import { StatSemanticEntry } from '@/data/types'
import { ParamStat, Stat } from '@/types/stats'
import Data from 'data'
import { checkCondition } from './mods'

type Reducer = {
  initial: number
  step: (x: number, y: number) => number
}

export type Environment = {
  target?: Set<string>
  global?: Stats
}

type StatEntry = {
  stat: string
  value: number
  param?: string
  tags?: (string | string[])[]
  gem_tags?: (string | string[])[]
}

type AggregateStat = {
  [key in Stat]?: key extends ParamStat ? { [key in string]?: number } : number
}

/**
 * Proxy for calculating total stats in some manner.
 * Uses lazy calculations.
 */
class StatAggregator implements AggregateStat {
  static reducers = {
    additive: { initial: 0, step: (x: number, y: number) => x + y },
    multiplicative: {
      initial: 1,
      step: (x: number, y: number) => x * (1 + 0.01 * y)
    },
    maximum: { initial: 0, step: (x: number, y: number) => Math.max(x, y) }
  }

  /**
   * Constructor.
   * @param {Stats} list StatList object.
   * @param {object} reducer Reducer object {initial: any, step: (*, *) => *}.
   * @param {Set<string>} tags Tag set.
   */
  constructor(list: Stats, reducer: Reducer, tags: Set<string>) {
    Object.defineProperties(this, {
      list: { enumerable: false, value: list },
      reducer: { enumerable: false, value: reducer },
      tags: { enumerable: false, value: tags }
    })
  }

  [key: string]: number | Record<string, number>

  /**
   * Register a new stat type.
   * @param {string} name Stat name.
   * @param {boolean} params True if stat has a parameter.
   */
  static register(name: string, params: boolean) {
    if (name in this.prototype) return
    Object.defineProperty(this.prototype, name, {
      get(this: StatAggregator) {
        let list = this.list as any as Stats | undefined
        const reducer = this.reducer as any as Reducer
        const tags = this.tags as any as Set<string>
        if (params) {
          const value: Record<string, number> = {}
          while (list) {
            if (name in list.values) {
              for (const stat of list.values[name]!) {
                if (!stat.tags || checkTags(stat.tags, tags)) {
                  value[stat.param!] = reducer.step(value[stat.param!] ?? reducer.initial, stat.value)
                }
              }
            }
            list = list.parent
          }
          Object.defineProperty(this, name, { value })
          return value
        } else {
          let value = reducer.initial
          while (list) {
            if (name in list.values) {
              for (const stat of list.values[name]!) {
                if (!stat.tags || checkTags(stat.tags, tags)) {
                  value = reducer.step(value, stat.value)
                }
              }
            }
            list = list.parent
          }
          Object.defineProperty(this, name, { value })
          return value
        }
      }
    })
  }
}

/**
 * Generic stat storage.
 * Stats can be accessed directly (raw stats), or through aggregators.
 */
class Stats {
  private aggregators!: Record<string, AggregateStat>

  additive!: AggregateStat
  parent?: Stats

  constructor() {
    Object.defineProperty(this, 'aggregators', {
      enumerable: false,
      value: {}
    })
    // has to be called separately since `aggregate` uses `aggregators`
    Object.defineProperty(this, 'additive', {
      enumerable: false,
      value: this.aggregate('additive')
    })
  }

  values: {
    [key in string]?: StatEntry[] & {
      [key in string]?: StatEntry[]
    }
  } = {}

  /**
   * Add a new stat to the list. This will take ownership of the stat object and modify it.
   * @param {*} data
   */
  addValue(data: StatEntry) {
    // add a new property to all stat aggregators
    StatAggregator.register(data.stat, !!data.param)

    if (!this.values[data.stat]) this.values[data.stat] = [] as any
    let dst = this.values[data.stat]!
    dst.push(data)
    if (data.param) {
      if (!dst[data.param]) {
        Object.defineProperty(dst, data.param, {
          enumerable: false,
          value: []
        })
      }
      dst[data.param]!.push(data)
    }
  }

  /**
   * Get an aggregator function of given type/tags.
   * @param {string} type Reducer type.
   * @param {Set<string>} tags Tag Set.
   */
  aggregate(type: keyof typeof StatAggregator.reducers, tags?: Set<string>): AggregateStat {
    const hash = type + (tags ? ':' + [...tags].sort().join('+') : '')
    if (this.aggregators[hash]) return this.aggregators[hash]
    return (this.aggregators[hash] = new StatAggregator(this, StatAggregator.reducers[type], tags ?? new Set()))
  }
}

function checkTags(list: (string | string[])[], tags: Set<string>) {
  return list.every((item) => {
    if (Array.isArray(item)) return item.some((sub) => tags.has(sub))
    return tags.has(item)
  })
}

class LocalStats extends Stats {
  private env?: Environment
  private tags!: Set<string>

  raw: Record<string, number> = {}

  constructor(env?: Environment, tags?: Set<string>) {
    super()
    Object.defineProperties(this, {
      env: { enumerable: false, value: env },
      tags: { enumerable: false, value: tags ?? new Set() }
    })
  }

  private add_(data: StatSemanticEntry, value: number) {
    if (data.required && !checkTags(data.required, this.tags)) return
    if (data.conditions && !checkCondition(data.conditions, value)) return
    if (data.target && !checkTags(data.target, this.env?.target ?? new Set())) return
    const to_add: StatEntry = {
      stat: data.stat,
      value: data.value ?? value
    }
    if (data.param) to_add.param = data.param
    if (data.tags) to_add.tags = data.tags
    if (data.gem_tags) to_add.gem_tags = data.gem_tags
    if (data.local) {
      this.addValue(to_add)
    } else {
      this.env?.global?.addValue(to_add)
    }
  }

  /**
   * Add a given stat/value pair (or just stat)
   * @param {object} stat
   * @param {number} [value]
   */
  add(stat: string, value: number) {
    this.raw[stat] = (this.raw[stat] ?? 0) + value
    const data = Data.semantics[stat]
    if (data instanceof Array) {
      data.forEach((stat) => this.add_(stat, value))
    } else if (data) {
      this.add_(data, value)
    }
  }
}

export { LocalStats, StatAggregator, Stats }
export default Stats
